import { doApi } from './apiClient';
import type {
  QuizCurrentResponse,
  QuizCooldownResponse,
  QuizAnswerResult,
  QuizLeaderboardEntry,
  AdminQuiz,
} from '../types/models/quiz';

export type QuizCurrentResult =
  | { status: 'available'; data: QuizCurrentResponse }
  | { status: 'cooldown'; available_at: string };

export const apiGetCurrentQuiz = async (): Promise<QuizCurrentResult> => {
  const data = await doApi<any>('api/quiz/current', 'GET');

  if (data?.available_at) {
    return { status: 'cooldown', available_at: data.available_at };
  }

  if (data?.attempt_id && data?.question) {
    return { status: 'available', data: data as QuizCurrentResponse };
  }

  throw new Error('Unexpected quiz response');
};

export const apiAnswerQuiz = (attemptId: number, choiceId: number): Promise<QuizAnswerResult> =>
  doApi<QuizAnswerResult>(`api/quiz/${attemptId}/answer`, 'POST', { choice_id: choiceId });

export const apiSkipQuiz = (attemptId: number): Promise<{ available_at: string }> =>
  doApi<{ available_at: string }>(`api/quiz/${attemptId}/skip`, 'POST');

export const apiGetQuizLeaderboard = (
  period: 'all' | 'week' | 'month' = 'all',
  limit = 50
): Promise<QuizLeaderboardEntry[]> =>
  doApi<QuizLeaderboardEntry[]>(`api/quiz/leaderboard?period=${period}&limit=${limit}`, 'GET');

// ─── Admin ────────────────────────────────────────────────────────────────────

export const apiAdminGetQuizzes = (): Promise<AdminQuiz[]> =>
  doApi<AdminQuiz[]>('api/admin/quiz', 'GET');

export const apiAdminCreateQuiz = (body: {
  question: string;
  is_active: boolean;
  is_priority: boolean;
  choices: { text: string; is_correct: boolean }[];
}): Promise<AdminQuiz> =>
  doApi<AdminQuiz>('api/admin/quiz', 'POST', body);

export const apiAdminUpdateQuiz = (
  id: number,
  body: Partial<{
    question: string;
    is_active: boolean;
    is_priority: boolean;
    choices: { text: string; is_correct: boolean }[];
  }>
): Promise<AdminQuiz> =>
  doApi<AdminQuiz>(`api/admin/quiz/${id}`, 'PATCH', body);

export const apiAdminDeleteQuiz = (id: number): Promise<void> =>
  doApi<void>(`api/admin/quiz/${id}`, 'DELETE');
